import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { buildMenu, NavLink, NavSection } from '../shell/nav-menu';
import { ShellService } from '../shell/shell.service';

export interface SettingsPage {
    link: NavLink;
    group: string;
}

// Settings pages with sub-pages outside their own route: { '/page': ['/other-route'] }.
const SUB_ROUTES: { [route: string]: string[] } = {};

/**
 * The Settings workspace's list: the sidebar's own Settings sections (nav-menu.ts), so names,
 * links and who sees what stay in one place.
 */
@Injectable({
    providedIn: 'root'
})
export class SettingsMenuService {
    /** localStorage key: the settings page last opened (Settings reopens it). */
    static readonly LAST_PAGE_KEY = 'im-settings-last-page';
    static readonly DEFAULT_PAGE = '/settings/system';

    readonly sections$: Observable<NavSection[]>;

    /** Remember an opened settings page (an Angular route in this user's settings list). */
    static remember(route: string) {
        try {
            localStorage.setItem(SettingsMenuService.LAST_PAGE_KEY, route);
        } catch (e) { /* private mode / storage blocked: Settings then opens System Settings */ }
    }

    /**
     * Where /settings opens: the page last opened if this user still has it, else System
     * Settings, else the first settings page they can open.
     */
    static landing(sections: NavSection[]): string {
        let last = null;
        try {
            last = localStorage.getItem(SettingsMenuService.LAST_PAGE_KEY);
        } catch (e) { }
        if (last && SettingsMenuService.find(sections, last)) {
            return last;
        }
        if (SettingsMenuService.find(sections, SettingsMenuService.DEFAULT_PAGE)) {
            return SettingsMenuService.DEFAULT_PAGE;
        }
        for (const s of sections) {
            const link = s.links.find(l => !!l.route);
            if (link) {
                return link.route;
            }
        }
        return null;
    }

    constructor(private shell: ShellService) {
        const user = this.shell.user || {};
        this.sections$ = this.shell.permissions$.pipe(
            startWith([] as string[]),
            map(permissions => {
                const settings = buildMenu({
                    isInspector: user.user_level == 'inspector',
                    isAdministrator: user.user_level == 'administrator',
                    inspectionTabOrder: undefined,
                    apiSite: environment.apiSite,
                    oldSite: environment.oldSite,
                    permissions
                }).filter(entry => entry.id === 'settings')[0];
                return settings ? settings.sections : [];
            })
        );
    }

    /** The settings page a URL is (or is under); null when it is not a settings page. */
    static find(sections: NavSection[], url: string): SettingsPage {
        const path = (url || '').split('?')[0].split('#')[0];
        for (const section of sections) {
            for (const link of section.links) {
                if (!link.route) {
                    continue;
                }
                const routes = [link.route].concat(SUB_ROUTES[link.route] || []);
                if (routes.some(r => path === r || path.indexOf(r + '/') === 0)) {
                    return { link, group: section.title };
                }
            }
        }
        return null;
    }

    /** /settings and /settings/<key> always belong to the workspace. */
    static isWorkspaceUrl(url: string): boolean {
        const path = (url || '').split('?')[0].split('#')[0];
        return path === '/settings' || path.indexOf('/settings/') === 0;
    }
}
